import { Box, Tab, Tabs } from "@mui/material";
import React, { useEffect, useState, useMemo } from "react";
import {
  AlertComponent,
  BreadcrumbComponent,
} from "../../../common/components";
import ActiveUsers from "./active-users/active-users";
import PendingUsersActions from "./pending-user-actions/pending-users-actions";
import "./user-reports.scss";
import { ApexOptions } from "apexcharts";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { getManageReports, addUserReports } from "../../../api/user-management";
import CustomRadioGroup from "../../../common/components/radio-button-component/radio-button-component";
import { useSetLoading } from "../../../common/hooks/appearance";
import OnboardedDateFilter from "../../tenant/tenant-list/tenant-filter-options/onboardeddate-filter";
import UserPerformance from "./user-performance/user-performance";
import { Form, Formik } from "formik";
import { useLogin } from "../../../common/hooks/useUser";
import CompanyReportFilter from "../../../common/components/company-report-filter/company-report-filter";
import partnerReportEmptyIcon from "../../../assets/icons/partner-reports-default.svg";
import EmailSetUpBanner from "../../../common/components/email-setup-required-banner/email-setup-banner";
import { useResourcePermissions } from "../../../common/hooks/resource-permissions";
import MODULES from "../../../constants/modules";

const crumbMapping = [
  {
    label: "User Management",
    link: "#",
    handleClick: () => {},
  },
  { label: "User Reports", link: "#" },
];

interface ActiveUsersProps {
  series: number[];
  options: ApexOptions; // ApexOptions type can be used if available
}
const UserReports = () => {
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [reportsData, setReportsData] = useState<any>(null);
  const [filterDate, setFilterDate] = useState<any>({});
  const [activeUsersData, setActiveUsersData] = useState<any>([]);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);

  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [indexValue, setIndexValue] = React.useState(0);
  const [partnerId, setPartnerId] = useState<number | null>(null);
  const [partnerName, setPartnerName] = useState<string | null>(null);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setIndexValue(newValue);
    if (newValue === 0) {
      setPartnerId(null);
    }
  };
  const loginDetails = useLogin();
  const tenantDetails = loginDetails?.tenantDetails;
  const userDetails = loginDetails?.userDetails;
  const isEmailConfigured = loginDetails?.isEmailConfigured;
  const isAFPSuperAdmin =
    tenantDetails?.tenantType === "SuperTenant" &&
    userDetails?.roles?.role === "Super Admin";
  const {canExport} = useResourcePermissions(MODULES.EXPORT);
  const renderTabContent = () => {
    switch (indexValue) {
      case 0:
        return <>{renderReports()}</>;
      case 1:
        return (
          <>
            <div className="partner-report-company-filter">
              <CompanyReportFilter
                setCompany={(partner) => {
                  setPartnerId(partner?.id);
                  setPartnerName(partner?.name);
                }}
              />
            </div>
            {partnerId ? (
              <>{renderReports()}</>
            ) : (
              <Box className="partner-report-empty-state">
                <img src={partnerReportEmptyIcon} alt="No Partner Selected" />
              </Box>
            )}
          </>
        );
      default:
        return <>{renderReports()}</>;
    }
  };

  const fetchManageReports = async () => {
    setLoading(true);
    try {
      const response = await getManageReports({
        startDate: filterDate?.start,
        endDate: filterDate?.end,
        partnerId: partnerId,
      });
      setReportsData(response?.data?.data);
      if (response?.data?.data?.activeInactiveUsers?.length > 0) {
        setActiveUsersData([
          response?.data?.data?.activeInactiveUsers?.find(
            (data: any) => data?.status === "ACTIVE"
          )?.percentage || 0,
          response?.data?.data?.activeInactiveUsers?.find(
            (data: any) => data?.status === "INACTIVE"
          )?.percentage || 0,
        ]);
      } else {
        setActiveUsersData([]);
      }
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to fetch reports"
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };
  const handleAddLeadReports = async (data: any, params: any) => {
    setLoading(true);
    try {
      await addUserReports(data, { ...params, partnerId });
      setAlertMessage(
        partnerName
          ? `${partnerName} Report generated successfully`
          : "Report generated successfully"
      );
      setAlertType("success");
      setAlertOpen(true);
    } catch (error: any) {
      setAlertMessage(error?.response?.data?.message || "Failed to fetch");
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const shouldFetch =
      (!filterDate.start && !filterDate.end) ||
      (filterDate.start && filterDate.end);
    if (shouldFetch) {
      fetchManageReports();
    }
  }, [filterDate, partnerId]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  const activeUsers: ActiveUsersProps = {
    series: [],

    options: {
      chart: {
        type: "donut" as const,
        id: "active-inactive-users",
        toolbar: {
          show: false,
          tools: {
            download: true,
          },
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "60%",
          },
        },
      },
      responsive: [
        {
          options: {
            chart: {
              width: "100%",
            },
          },
        },
        {
          breakpoint: 1024,
          options: {
            legend: {
              position: "bottom",
              horizontalAlign: "left",
              with: "100%",
            },
          },
        },
      ],
      labels: ["Active", "Inactive"],
      dataLabels: {
        enabled: false,
        style: {
          fontSize: "14px",
          fontWeight: "bold",
        },
      },
      legend: {
        horizontalAlign: "left",
        fontSize: "14px",
        position: "right",
        formatter: function (val, opts) {
          return val + " - " + `${opts.w.globals.series[opts.seriesIndex]}%`;
        },
        floating: false,

        itemMargin: {
          horizontal: 10,
          vertical: 6,
        },
        markers: {
          size: 5,
          strokeWidth: 0,
          offsetX: -5,
        },
        width: 220,
      },

      tooltip: {
        enabled: true,
        y: {
          formatter: (value, index) => {
            return `${value}% ( ${reportsData?.activeInactiveUsers?.find((data: any) => data?.percentage === value)?.count || 0} )`;
          },
        },
      },
      colors: ["#065E10", "#3B3B3B"],
    },
  };

  const renderReports = () => {
    return (
      <>
        <Box className="active-performance-wrapper" gap={3}>
          <ActiveUsers
            series={activeUsersData}
            options={activeUsers?.options}
            handleAddLeadReports={handleAddLeadReports}
            title="Active vs Inactive Users"
            enableExport={isEmailConfigured && canExport}
          />
          <PendingUsersActions
            userPendingActionsData={reportsData?.userPendingActions}
            handleAddLeadReports={handleAddLeadReports}
            enableExport={isEmailConfigured && canExport}
          />
        </Box>
        <Box className="active-performance-wrapper" gap={3}>
          <UserPerformance
            userPerformanceData={reportsData?.userPerformance}
            handleAddLeadReports={handleAddLeadReports}
            enableExport={isEmailConfigured && canExport}
          />
        </Box>
      </>
    );
  };

  return (
    <Box className="user-reports-container">
      <Box>
        <BreadcrumbComponent breadcrumbLink={crumbMapping} />
      </Box>
      <Box className="user-reports-content">
        <Box className="user-reports-filter">
          <Formik
            initialValues={{
              filterItem: "Yes",
              onBoardedAtStart: null,
              onBoardedAtEnd: null,
            }}
            enableReinitialize
            // validate={validate}
            onSubmit={() => {}}
          >
            {({
              values,
              errors,
              touched,
              setFieldValue,
              handleSubmit,
              resetForm,
            }) => {
              return (
                <Box className="user-reports-filter-item" gap={3}>
                  <p>Date</p>
                  <Box
                    className="filter-value"
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                  >
                    {values?.filterItem === "Yes"
                      ? "Last 30 days "
                      : "Date range"}
                    {isFilterOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                    {isFilterOpen && (
                      <Box
                        className="filter-dropdown"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Form
                          className="drawer-content"
                          onSubmit={handleSubmit}
                        >
                          <Box>
                            <CustomRadioGroup
                              name="filterItem"
                              value={values?.filterItem}
                              options={[
                                { label: "Last 30 days", value: "Yes" },
                                { label: "Date range", value: "No" },
                              ]}
                              onChange={(e) => {
                                if (e.target.value === "Yes") {
                                  setFilterDate({});
                                  setFieldValue("onBoardedAtStart", null);
                                  setFieldValue("onBoardedAtEnd", null);
                                }
                                setFieldValue("filterItem", e.target.value);
                              }}
                            />
                            {values?.filterItem === "No" && (
                              <OnboardedDateFilter
                                startDate={values.onBoardedAtStart}
                                endDate={values.onBoardedAtEnd}
                                onDateRangeChange={(start, end) => {
                                  setFilterDate({
                                    start: start ? new Date(start) : null,
                                    end: end ? new Date(end) : null,
                                  });
                                  if (start)
                                    setFieldValue?.("onBoardedAtStart", start);
                                  if (end)
                                    setFieldValue?.("onBoardedAtEnd", end);
                                }}
                                startDateError={
                                  touched?.onBoardedAtStart &&
                                  errors?.onBoardedAtStart
                                    ? String(errors.onBoardedAtStart)
                                    : undefined
                                }
                                endDateError={
                                  touched?.onBoardedAtEnd &&
                                  errors?.onBoardedAtEnd
                                    ? String(errors.onBoardedAtEnd)
                                    : undefined
                                }
                              />
                            )}
                          </Box>
                        </Form>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            }}
          </Formik>
        </Box>
        {isAFPSuperAdmin ? (
          <>
            <Box>
              <Tabs
                value={indexValue}
                onChange={handleTabChange}
                allowScrollButtonsMobile
                className="lead-reports-tabs"
                sx={{
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 500,
                  },
                  "& .MuiTab-root.Mui-selected": {
                    color: "black",
                    borderBottom: "2px solid #785607",
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#785607",
                  },
                }}
              >
                <Tab iconPosition="start" label="AFP Reports" />
                <Tab iconPosition="start" label="Partner Reports" />
              </Tabs>
            </Box>
            {!isEmailConfigured && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <EmailSetUpBanner />
              </div>
            )}
            <Box>{renderTabContent()}</Box>
          </>
        ) : (
          <>
            {!isEmailConfigured && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <EmailSetUpBanner />
              </div>
            )}
            <Box>{renderReports()}</Box>
          </>
        )}
      </Box>
      <AlertComponent
        message={alertMessage}
        type={alertType ?? "info"}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </Box>
  );
};

export default UserReports;
